import { useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { DateTime as DT } from 'luxon';
import { useTranslation } from 'react-i18next';
import SolarIcon from 'components/icons/SolarIcon';

export type TaskCalendarCreateMenuProps = {
    x: number;
    y: number;
    dateMillis: number;
    allDay: boolean;
    onCreate: () => void;
    onHide: () => void;
};

const formatCreateDueLabel = (dateMillis: number, allDay: boolean): string => {
    const dt = DT.fromMillis(dateMillis, { zone: 'local' });
    if (!dt.isValid) return '';
    return allDay ? dt.toFormat('dd.MM.yyyy') : dt.toFormat('dd.MM.yyyy HH:mm');
};

const TaskCalendarCreateMenu = ({
    x,
    y,
    dateMillis,
    allDay,
    onCreate,
    onHide,
}: TaskCalendarCreateMenuProps) => {
    const { t } = useTranslation(['Tasks']);
    const ref = useRef<HTMLDivElement>(null);
    const dueLabel = formatCreateDueLabel(dateMillis, allDay);

    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const pad = 8;
        let left = x;
        let top = y;
        if (left + rect.width > window.innerWidth - pad) {
            left = Math.max(pad, window.innerWidth - rect.width - pad);
        }
        if (top + rect.height > window.innerHeight - pad) {
            top = Math.max(pad, window.innerHeight - rect.height - pad);
        }
        el.style.left = `${left}px`;
        el.style.top = `${top}px`;
    }, [x, y]);

    useEffect(() => {
        const onPointerDown = (event: PointerEvent) => {
            if (ref.current?.contains(event.target as Node)) return;
            onHide();
        };
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onHide();
        };
        document.addEventListener('pointerdown', onPointerDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('pointerdown', onPointerDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [onHide]);

    return createPortal(
        <div
            ref={ref}
            className="dropdown-menu show task-calendar__create-menu"
            style={{ position: 'fixed', top: y, left: x }}
            role="menu"
        >
            <button
                type="button"
                className="dropdown-item d-flex align-items-start"
                role="menuitem"
                onClick={onCreate}
            >
                <SolarIcon name="plus" className="me-2 mt-1" aria-hidden="true" />
                <span className="d-flex flex-column text-start min-w-0">
                    <span className="fw-medium">{t('calendar.createTask')}</span>
                    {dueLabel ? (
                        <small className="text-muted">{dueLabel}</small>
                    ) : null}
                </span>
            </button>
        </div>,
        document.body,
    );
};

export default TaskCalendarCreateMenu;
